import type { ReactElement } from 'react';
import { Box, Group, SegmentedControl, Text } from '@mantine/core';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatUsd } from '@/shared/analytics/MetricFormatting';
import { SurfaceCard } from '@/design/SurfaceCard';
import { type VizAccent } from '@/design/VizAccents';
import type { ForecastResult, MonthComparison } from './CostForecast';

const TOOLTIP_STYLE = {
    background: 'var(--bm-color-body)',
    border: '1px solid var(--bm-color-border)',
    borderRadius: 'var(--bm-radius)',
    fontSize: 12,
};

const LOOKBACK_OPTIONS = [
    { label: 'This month', value: '1' },
    { label: '3 months', value: '3' },
    { label: '6 months', value: '6' },
];

/** Distinct brand shades so prior-month context lines can be told apart (the current month leads in the full brand accent). */
const PRIOR_RAMP = Array.from({ length: 6 }, (_, index) => `var(--bm-chart-${index + 2})`);

interface CostProjectionChartProps {
    result: ForecastResult;
    comparison: MonthComparison[];
    /** @deprecated Colours are brand-based now; accent is ignored. */
    accent?: VizAccent;
    lookbackMonths: number;
    onLookbackChange: (months: number) => void;
}

function dayOf(iso: string): number {
    return Number(iso.slice(8, 10));
}

/** Cumulative current-month spend (solid) with a dashed projection to month-end, over faint prior-month curves for context. Colours follow the page accent; the look-back control sizes how many months show. */
export default function CostProjectionChart({ result, comparison, lookbackMonths, onLookbackChange }: CostProjectionChartProps): ReactElement {
    const solid = 'var(--bm-color-brand-accent)';

    const currentByDay = new Map<number, { actual: number | null; projected: number | null }>();
    for (const point of result.cumulative) currentByDay.set(dayOf(point.date), { actual: point.actual, projected: point.projected });
    const comparisonByDay = comparison.map((month) => ({ label: month.label, values: new Map(month.points.map((point) => [point.day, point.value])) }));

    const maxDay = Math.max(1, ...result.cumulative.map((point) => dayOf(point.date)), ...comparison.flatMap((month) => month.points.map((point) => point.day)));
    const data: Record<string, number | null>[] = [];
    for (let day = 1; day <= maxDay; day += 1) {
        const row: Record<string, number | null> = { day };
        const current = currentByDay.get(day);
        row.actual = current?.actual ?? null;
        row.projected = current?.projected ?? null;
        for (const month of comparisonByDay) row[month.label] = month.values.get(day) ?? null;
        data.push(row);
    }

    return (
        <SurfaceCard $padding="md">
            <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                    Spend trajectory
                </Text>
                <SegmentedControl size="xs" value={String(lookbackMonths)} onChange={(value) => onLookbackChange(Number(value))} data={LOOKBACK_OPTIONS} />
            </Group>
            <Box style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bm-color-border)" vertical={false} />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" />
                        <YAxis tickFormatter={(value: number) => formatUsd(value)} tick={{ fontSize: 11 }} stroke="var(--bm-color-text-dimmed)" width={72} />
                        <Tooltip formatter={(value: number) => formatUsd(value)} labelFormatter={(day: number) => `Day ${day}`} contentStyle={TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {comparisonByDay.map((month, index) => (
                            <Line key={month.label} type="monotone" dataKey={month.label} name={month.label} stroke={PRIOR_RAMP[index % PRIOR_RAMP.length] ?? solid} strokeWidth={1.5} dot={false} connectNulls legendType="plainline" />
                        ))}
                        <Line type="monotone" dataKey="actual" name="This month" stroke={solid} strokeWidth={2.5} dot={false} connectNulls legendType="plainline" />
                        <Line type="monotone" dataKey="projected" name="Projected" stroke={solid} strokeWidth={2.5} strokeDasharray="5 4" dot={false} connectNulls legendType="plainline" />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </SurfaceCard>
    );
}
